import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PageSeo from "../../components/common/PageSeo";
import DecisionReceipt from "../../components/home/DecisionReceipt";
import NotFoundPage from "../static/NotFoundPage";
import api from "../../utils/api/axiosInstance";
import { buildPickReason } from "../../utils/decisionPickCopy";
import { toPublicDecisionReason } from "../../utils/publicDecisionCopy";
import styles from "../../styles/pages/decision/PublicDecisionPage.module.css";

const RECEIPT_TIMEOUT_MS = 8000;

const PublicDecisionPage = () => {
  const { shareToken } = useParams();
  const [receipt, setReceipt] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), RECEIPT_TIMEOUT_MS);
    setReceipt(null);
    setMissing(false);
    api
      .get(`/decisions/${shareToken}`, { signal: controller.signal })
      .then((res) => {
        if (cancelled) return;
        if (!res.data?.receipt) {
          setMissing(true);
          return;
        }
        setReceipt(res.data.receipt);
      })
      .catch(() => {
        if (!cancelled) setMissing(true);
      })
      .finally(() => clearTimeout(timer));
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [shareToken]);

  if (missing) return <NotFoundPage />;

  const product = receipt?.product
    ? {
        id: receipt.product.id,
        name: receipt.product.name,
        price: receipt.product.price,
        images: receipt.product.image ? [receipt.product.image] : [],
      }
    : null;
  const publicReason = product
    ? toPublicDecisionReason(
        buildPickReason({
          product,
          advice: receipt.reason || "",
        }),
      )
    : "";

  return (
    <div className={styles.page}>
      <PageSeo
        title={
          product?.name
            ? `${product.name} — a Simpary decision`
            : "A Simpary decision"
        }
        description={(publicReason || "Someone picked this on Simpary.").slice(0, 180)}
        pathname={`/d/${shareToken}`}
      />
      <div className={styles.inner}>
        {product ? (
          <DecisionReceipt
            product={product}
            reason={receipt.reason}
            tradeoff={receipt.tradeoff}
            offers={receipt.offers || []}
            publicView
          />
        ) : (
          <p className={styles.loading}>Loading this decision…</p>
        )}
      </div>
    </div>
  );
};

export default PublicDecisionPage;
